import { useEffect, useState } from "react";
import { getRelOrcamentos } from "@/services/report";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Select, Button, Input } from "antd";
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { mascaraCelular } from "@/utils/masks";
import "@/styles/globals.css";

interface RelOrcamentosItem {
  unidade: string;
  /** Sequencial do orçamento — orçamento não tem número de matrícula. */
  idOrcamento: number | null;
  nomeResponsavel: string;
  telefoneResponsavel: string;
  emailResponsavel: string;
  turma: string;
  especificas: string;
  valorTotal: number;
  dataCadastro: string;
}

interface TotalRow {
  unidade: string;
  idOrcamento: null;
  nomeResponsavel: string;
  telefoneResponsavel: string;
  emailResponsavel: string;
  turma: string;
  especificas: string;
  valorTotal: number;
  dataCadastro: string;
  isTotal: true;
}

type TableDataType = RelOrcamentosItem | TotalRow;

interface Opcao {
  id: number;
  nome: string;
}

// Valor sentinela da opção "Todos" nos filtros multi-seleção
const TODOS = "";

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export default function RelOrcamentos() {
  const [unidades, setUnidades] = useState<Opcao[]>([]);
  const [turmas, setTurmas] = useState<Opcao[]>([]);
  const [filtroUnidades, setFiltroUnidades] = useState<(number | string)[]>([TODOS]);
  const [filtroTurmas, setFiltroTurmas] = useState<(number | string)[]>([TODOS]);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<RelOrcamentosItem[]>([]);

  useEffect(() => {
    getUnidades().then((res) => setUnidades(res.data || []));
    getTurma().then((res) => setTurmas(res.data || []));
  }, []);

  // Mantém a opção "Todos" exclusiva: selecionar "Todos" limpa as demais e vice-versa
  const handleMultiSelect = (values: (number | string)[], setter: (v: (number | string)[]) => void) => {
    if (values.length === 0) {
      setter([TODOS]);
      return;
    }
    const ultimo = values[values.length - 1];
    if (ultimo === TODOS) {
      setter([TODOS]);
      return;
    }
    setter(values.filter((v) => v !== TODOS));
  };

  const buscar = async () => {
    if (!dataInicio || !dataFim) {
      alert("Preencha Data Início e Data Fim.");
      return;
    }
    if (new Date(dataInicio) > new Date(dataFim)) {
      alert("Data Início deve ser menor que Data Fim.");
      return;
    }
    setLoading(true);
    const params: { dataInicial: string; dataFinal: string; unidadeIds?: string; turmaIds?: string } = {
      dataInicial: dataInicio,
      dataFinal: dataFim,
    };
    if (!filtroUnidades.includes(TODOS) && filtroUnidades.length > 0) {
      params.unidadeIds = filtroUnidades.join(",");
    }
    if (!filtroTurmas.includes(TODOS) && filtroTurmas.length > 0) {
      params.turmaIds = filtroTurmas.join(",");
    }
    const res = await getRelOrcamentos(params);
    setDados(res?.data || []);
    setLoading(false);
  };

  const descricaoUnidades = filtroUnidades.includes(TODOS)
    ? "Todas"
    : unidades.filter((u) => filtroUnidades.includes(u.id)).map((u) => u.nome).join(", ") || "Todas";

  const descricaoTurmas = filtroTurmas.includes(TODOS)
    ? "Todas"
    : turmas.filter((t) => filtroTurmas.includes(t.id)).map((t) => t.nome).join(", ") || "Todas";

  const formatarData = (data: string) => {
    if (!data) return "";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const descricaoPeriodo = `${formatarData(dataInicio)} a ${formatarData(dataFim)}`;

  const valorTotalGeral = dados.reduce((acc, item) => acc + (item.valorTotal || 0), 0);

  const montarLinhaExportacao = (item: RelOrcamentosItem) => ({
    "Unidade": item.unidade || "",
    "ID": item.idOrcamento != null ? String(item.idOrcamento) : "",
    "Nome do Responsável": item.nomeResponsavel || "",
    "Tel Responsável": mascaraCelular(item.telefoneResponsavel),
    "E-mail Responsável": item.emailResponsavel || "",
    "Turma": item.turma || "",
    "Específicas": item.especificas || "",
    "Valor Total": item.valorTotal || 0,
  });

  const exportToExcel = () => {
    const exportData: any[] = dados.map(montarLinhaExportacao);
    exportData.push({
      "Unidade": "Total",
      "ID": "",
      "Nome do Responsável": "",
      "Tel Responsável": "",
      "E-mail Responsável": "",
      "Turma": "",
      "Específicas": "",
      "Valor Total": valorTotalGeral,
    });

    // Cabeçalho dos filtros
    const header = [[`Período: ${descricaoPeriodo}`, `Unidades: ${descricaoUnidades}`, `Turmas: ${descricaoTurmas}`]];
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, exportData, { origin: -1 });

    // Formata a coluna "Valor Total" como moeda
    const range = XLSX.utils.decode_range(ws['!ref'] as string);
    for (let row = 2; row <= range.e.r; row++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: 7 });
      const cell = ws[cellRef];
      if (cell && typeof cell.v === "number") {
        cell.z = 'R$ #,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelOrcamentos");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "relatorio_orcamentos.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("Relatório de Orçamentos", 14, 14);
    doc.setFontSize(11);
    doc.text(`Período: ${descricaoPeriodo}`, 14, 22);
    doc.text(`Unidades: ${descricaoUnidades}`, 80, 22);
    doc.text(`Turmas: ${descricaoTurmas}`, 190, 22);

    const head = [
      "Unidade", "ID", "Nome do Responsável", "Tel Responsável",
      "E-mail Responsável", "Turma", "Específicas", "Valor Total",
    ];

    const tableData = dados.map((item) => [
      item.unidade || "",
      item.idOrcamento != null ? String(item.idOrcamento) : "",
      item.nomeResponsavel || "",
      mascaraCelular(item.telefoneResponsavel),
      item.emailResponsavel || "",
      item.turma || "",
      item.especificas || "",
      currencyFormatter.format(item.valorTotal || 0),
    ]);
    tableData.push(["Total", "", "", "", "", "", "", currencyFormatter.format(valorTotalGeral)]);

    autoTable(doc, {
      head: [head],
      body: tableData,
      startY: 26,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fontSize: 7 },
      columnStyles: { 7: { halign: "right" } },
      margin: { left: 5, right: 5 },
      tableWidth: 'auto',
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save("relatorio_orcamentos.pdf");
  };

  const totalRow: TotalRow = {
    unidade: "Total",
    idOrcamento: null,
    nomeResponsavel: "",
    telefoneResponsavel: "",
    emailResponsavel: "",
    turma: "",
    especificas: "",
    valorTotal: valorTotalGeral,
    dataCadastro: "",
    isTotal: true,
  };

  const dadosTableComTotal: TableDataType[] = dados.length > 0 ? [...dados, totalRow] : [];

  const columns: ColumnsType<TableDataType> = [
    { title: "Unidade", dataIndex: "unidade", key: "unidade", width: 140 },
    {
      title: "ID",
      dataIndex: "idOrcamento",
      key: "idOrcamento",
      width: 110,
      align: "right",
      render: (idOrcamento: number | null) => (idOrcamento != null ? String(idOrcamento) : ""),
    },
    { title: "Nome do Responsável", dataIndex: "nomeResponsavel", key: "nomeResponsavel", width: 220 },
    {
      title: "Tel Responsável",
      dataIndex: "telefoneResponsavel",
      key: "telefoneResponsavel",
      width: 140,
      render: (tel: string) => mascaraCelular(tel),
    },
    { title: "E-mail Responsável", dataIndex: "emailResponsavel", key: "emailResponsavel", width: 220 },
    { title: "Turma", dataIndex: "turma", key: "turma", width: 160 },
    { title: "Específicas", dataIndex: "especificas", key: "especificas", width: 280 },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      width: 130,
      align: "right",
      render: (valor: number) => currencyFormatter.format(valor || 0),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Relatório de Orçamentos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <div>
            <label className="text-white block mb-1">Data Início</label>
            <Input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              style={{ width: 160 }}
            />
          </div>
          <div>
            <label className="text-white block mb-1">Data Fim</label>
            <Input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              style={{ width: 160 }}
            />
          </div>
          <Select
            mode="multiple"
            value={filtroUnidades}
            onChange={(values) => handleMultiSelect(values, setFiltroUnidades)}
            style={{ minWidth: 260 }}
            placeholder="Unidade"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Unidades" },
              ...unidades.map((u) => ({ value: u.id, label: u.nome })),
            ]}
          />
          <Select
            mode="multiple"
            value={filtroTurmas}
            onChange={(values) => handleMultiSelect(values, setFiltroTurmas)}
            style={{ minWidth: 260 }}
            placeholder="Turma"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Turmas" },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
          />
          <Button type="primary" loading={loading} onClick={buscar}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>Exportar Excel</Button>
          <Button onClick={exportToPDF} disabled={dados.length === 0}>Exportar PDF</Button>
        </div>
        <Table
          columns={columns}
          dataSource={dadosTableComTotal}
          rowKey={(row, idx) => `${row.idOrcamento ?? "sem-id"}-${idx}`}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1400 }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
          rowClassName={(record) => ("isTotal" in record ? "tabela-total-row" : "")}
        />
      </ConfigProvider>
    </>
  );
}
